import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Query } from '../../design-system/types';
import { useGetDossierOverviews } from '../dossier-overview/hooks';
import { NormalizedAgents } from '../agent/types';
import { NormalizedDossier, DossierId } from '../dossier/types';
import { useAuth } from '../../shared/providers/AuthProvider';
import { useGetAgents } from '../agent/hooks';

import { NormalizedInTripReview, AlertInTripReviewsInformation, InTripReview } from './types';
import { fetchInTripReviews } from './api';

export const getInTripReviewsQueryKey = () => ['inTripReview'];

const useGetInTripReviews = () => {
    return useQuery({
        queryKey: getInTripReviewsQueryKey(),
        queryFn: () => fetchInTripReviews(),
    });
};

const getUnsatisfiedTravelersInTripReviews = (
    inTripReviews: NormalizedInTripReview,
    dossiers: NormalizedDossier,
    agents: NormalizedAgents,
    query: Query,
    userId: number | string | null,
): AlertInTripReviewsInformation[] => {
    return Object.entries(inTripReviews)
        .filter(([dossierId, inTripReview]: [DossierId, InTripReview]) => {
            if (
                Object.keys(dossiers).length === 0 ||
                !dossiers[dossierId] ||
                !dossiers[dossierId].agentId
            ) {
                return false;
            }

            const { agentId } = dossiers[dossierId];

            const filteredByConnectedAgent =
                !inTripReview.isTravelerSatisfied && userId?.toString() === agentId;

            if (!query.agentId) {
                return filteredByConnectedAgent;
            }

            const filteredByAgent = query.agentId !== 'all' && query.agentId?.includes(agentId);
            const filteredByAllAgents = query.agentId === 'all';

            return !inTripReview.isTravelerSatisfied && (filteredByAgent || filteredByAllAgents);
        })
        .map(([dossierId, inTripReview]: [DossierId, InTripReview]) => {
            const { project, agentId } = dossiers[dossierId];

            return {
                dossierId: dossierId,
                createdAt: inTripReview.createdAt,
                requesterName: project.requesterName,
                agentPhotoUrl: agents[agentId].photoUrl,
                agentName: agents[agentId].name,
            };
        })
        .sort((alertInTripReviewInformationA, alertInTripReviewInformationB) => {
            if (
                alertInTripReviewInformationA.createdAt &&
                alertInTripReviewInformationB.createdAt
            ) {
                return (
                    alertInTripReviewInformationB.createdAt.getTime() -
                    alertInTripReviewInformationA.createdAt.getTime()
                );
            }
            return 0;
        });
};

export const useGetAlertInTripReviewsInformation = (query: Query) => {
    const { data: dossiers } = useGetDossierOverviews();
    const { data: agents } = useGetAgents();
    const { data: inTripReviews } = useGetInTripReviews();

    const { userId } = useAuth();

    return useMemo(() => {
        if (!inTripReviews || !dossiers || !agents) return [];

        return getUnsatisfiedTravelersInTripReviews(inTripReviews, dossiers, agents, query, userId);
    }, [inTripReviews, dossiers, agents, query, userId]);
};
